import React, { useState, useEffect, useCallback } from 'react';
import { Button, Badge, Alert, Spinner, Table } from 'react-bootstrap';
import { CreditCard, Plus, Trash2, AlertTriangle, RefreshCw, Info, ListTree } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { useLocation, useHistory, Link } from 'react-router-dom';
import { fetchChartRoles } from '../../../services/api/chartRolesApi';
import SettingsCard from './SettingsCard';
import { EmptyState } from '../Common/EmptyState';
import {
    fetchConnections,
    fetchStatus,
    fetchNeedsReview,
    startConnect,
    disconnect,
} from '../../../services/ZettleService';

/**
 * ZettleSettings — connect a Zettle account so POS sales are booked automatically.
 *
 * Follows the Bankanslutningar card pattern (connect → full-page OAuth redirect →
 * backend 302s back with ?zettle_connected=…). Two things this surface has to do
 * that the bank one does not:
 *
 *   1. State the CUTOVER plainly. The sync cursor starts at the moment of
 *      connecting and history is never backfilled, because merchants have
 *      already booked past z-reports by hand and importing them would
 *      double-book the ledger. If the UI does not say "stop booking this
 *      manually from {date}", the merchant will book it twice — which is the
 *      single most likely way this integration causes damage.
 *   2. Show what was NOT handled. The pipeline deliberately refuses to book
 *      anything it cannot justify (gift cards, cash, split payments, unknown
 *      VAT rates, unreconciled payouts). Those are parked, not dropped, and
 *      this queue is where they surface — a silent parked queue would look
 *      exactly like a working integration while sales quietly pile up.
 */

const STATUS_BADGES = {
    // 'active' is the normal state — only exceptions are badged.
    paused: { variant: 'secondary', label: 'Pausad' },
    error: { variant: 'danger', label: 'Fel' },
    disconnected: { variant: 'secondary', label: 'Frånkopplad' },
};

const SUBSCRIPTION_BADGES = {
    // Zettle deletes a webhook subscription after sustained delivery failure and
    // never replays what it dropped. The 6-hourly sweep still keeps the books
    // correct, so this is a freshness warning, never a data-loss one.
    missing: { variant: 'warning', label: 'Direktavisering saknas' },
    error: { variant: 'warning', label: 'Direktavisering fel' },
    none: { variant: 'secondary', label: 'Endast periodisk hämtning' },
};

const CALLBACK_REASONS = {
    invalid_state: 'Ogiltig begäran — försök ansluta igen.',
    missing_code: 'Zettles svar var ofullständigt — försök igen.',
    exchange_failed: 'Kunde inte slutföra anslutningen mot Zettle.',
    company_not_found: 'Företaget kunde inte verifieras.',
    not_configured: 'Zettle-integrationen är inte aktiverad i den här miljön.',
    access_denied: 'Du avbröt kopplingen hos Zettle.',
};

/**
 * Human labels for the roles the mapper books through. Presentation only — the
 * ROLE LIST comes from the backend (`status.mapperRoles`, read straight from
 * the mapper) so it cannot drift from what is actually posted, and the
 * ACCOUNTS come from the shared chart-role endpoint. Neither is restated here.
 */
const ROLE_LABELS = {
    card_acquirer_receivable: 'Fordran kortinlösen',
    bank_fees: 'Transaktionsavgifter',
    primary_bank: 'Bankkonto (utbetalningar)',
    rounding: 'Öresutjämning',
    sales_domestic_25: 'Försäljning 25 % moms',
    sales_domestic_12: 'Försäljning 12 % moms',
    sales_domestic_6: 'Försäljning 6 % moms',
    vat_outgoing_domestic_25: 'Utgående moms 25 %',
    vat_outgoing_domestic_12: 'Utgående moms 12 %',
    vat_outgoing_domestic_6: 'Utgående moms 6 %',
};

/** Why an event was parked, in the merchant's language rather than ours. */
// The connection's own failures, keyed on `last_error_code`.
//
// `last_error` is a diagnostics column: the backend writes raw provider codes
// and interpolated internals into it ("Zettle-återkallelsen misslyckades:
// auth_failed", "purchases: connect ETIMEDOUT"), and this card printed it
// verbatim in an amber banner. `auth_failed` tells a merchant nothing and
// reads as something they broke. Say what happened and what, if anything,
// they should do; the raw text stays in the row and in the logs for support.
//
// Codes come from ZettleClient (invalid_grant, token_refresh_failed),
// ZettleSyncService (sync_failed), ZettleDailyCloseService (close_failed) and
// ZettleRepository.markDisconnected (revoke_failed).
const CONNECTION_ERRORS = {
    revoke_failed: {
        variant: 'warning',
        title: 'Kopplingen är borttagen här — kontrollera hos Zettle',
        body: 'Vi kunde inte återkalla behörigheten hos Zettle. LedgerLoop hämtar '
            + 'ingenting mer, men vill du återkalla den helt tar du bort LedgerLoop '
            + 'bland de anslutna apparna i ditt Zettle-konto.',
    },
    invalid_grant: {
        variant: 'warning',
        title: 'Anslutningen har upphört att gälla',
        body: 'Zettle godtar inte längre behörigheten. Anslut Zettle igen för att '
            + 'återuppta hämtningen — försäljning under tiden hämtas inte i efterhand.',
    },
    token_refresh_failed: {
        variant: 'warning',
        title: 'Kunde inte förnya behörigheten hos Zettle',
        body: 'Hämtningen är pausad och görs om automatiskt. Står det kvar, '
            + 'anslut Zettle igen.',
    },
    sync_failed: {
        variant: 'warning',
        title: 'Senaste hämtningen misslyckades',
        body: 'Nästa hämtning gör om försöket. Ingenting går förlorat — försäljningen '
            + 'ligger kvar hos Zettle tills den hämtats.',
    },
    close_failed: {
        variant: 'warning',
        title: 'Dagens verifikation kunde inte bokföras',
        body: 'Försäljningen är hämtad men inte bokförd. Nästa körning gör om '
            + 'försöket; står det kvar behöver någon titta på det.',
    },
};

const REVIEW_REASONS = {
    gift_card: 'Presentkort — en skuld, inte en intäkt',
    izettle_invoice: 'Zettle-faktura — regleras utanför kortutbetalningen',
    non_sek: 'Annan valuta än SEK',
    cash_payment: 'Kontantbetalning',
    split_payment: 'Delbetalt med flera betalmedel',
    no_vat_breakdown: 'Saknar momsuppdelning — kontrollera momssatserna i kassan',
    unsupported_vat_rate: 'Momssats som inte bokförs automatiskt',
    unattributed_amount: 'Momsraderna täcker inte hela köpet',
    unsupported_payment_type: 'Betalmedel som inte bokförs automatiskt',
    unknown_finance_type: 'Okänd transaktionstyp från Zettle',
    closed_period: 'Perioden är stängd',
    payout_residual: 'Utbetalningen stämmer inte med perioden',
    posting_rejected: 'Bokföringen avvisades',
    role_unsupported: 'Konto saknas i kontoplanen',
};

/**
 * Parse a datetime the API may hand back as a naive `'YYYY-MM-DD HH:MM:SS'`
 * string (the backend's pools use `dateStrings: true`).
 *
 * Two failures this avoids: Safari returns Invalid Date for that shape, and
 * date-fns `format()` THROWS on an invalid date — which would white-screen the
 * whole settings panel, not just this field. And even where it parses, it is
 * read as local time, so the cutover banner could show the wrong day. Mirrors
 * the backend's `fromDbTime`.
 */
const toDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    const raw = String(value).trim();
    const iso = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw) ? raw : `${raw.replace(' ', 'T')}Z`;
    const parsed = new Date(iso);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const fmtDate = (value) => { const d = toDate(value); return d ? format(d, 'd MMM yyyy', { locale: sv }) : '—'; };
const fmtDateTime = (value) => { const d = toDate(value); return d ? format(d, 'd MMM yyyy HH:mm', { locale: sv }) : 'Aldrig'; };
const fmtAmount = (minor) => (Number(minor || 0) / 100).toLocaleString('sv-SE', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
});

const ZettleSettings = ({ companyId }) => {
    const [connections, setConnections] = useState([]);
    const [statuses, setStatuses] = useState({});
    const [review, setReview] = useState({});
    const [roles, setRoles] = useState(null);
    const [loading, setLoading] = useState(true);
    const [connecting, setConnecting] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const location = useLocation();
    const history = useHistory();

    const load = useCallback(async () => {
        try {
            const rows = await fetchConnections(companyId);
            setConnections(rows);

            // Status and queue per connection. Failures here are non-fatal: the
            // connection list is the important part, and a missing count should
            // not blank the whole panel.
            const detail = await Promise.all(rows.map(async (row) => {
                try {
                    const [status, queue] = await Promise.all([
                        fetchStatus(companyId, row.connectionId),
                        fetchNeedsReview(companyId, row.connectionId, { limit: 20 }),
                    ]);
                    return [row.connectionId, status, queue];
                } catch {
                    return [row.connectionId, null, null];
                }
            }));
            setStatuses(Object.fromEntries(detail.map(([id, s]) => [id, s])));
            setReview(Object.fromEntries(detail.map(([id, , q]) => [id, q])));

            // Resolutions come from the SHARED chart-role endpoint, not a
            // Zettle-specific one — so what is shown here is exactly what every
            // other booking path resolves, and a re-pointing made elsewhere
            // shows up here too.
            if (rows.length) {
                try {
                    setRoles(await fetchChartRoles(companyId));
                } catch {
                    setRoles(null);
                }
            }
        } catch (err) {
            toast.error('Kunde inte hämta Zettle-anslutningar.');
        } finally {
            setLoading(false);
        }
    }, [companyId]);

    useEffect(() => { load(); }, [load]);

    // Handle the OAuth round-trip's landing. The param is consumed and removed
    // so a refresh doesn't re-toast.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const status = params.get('zettle_connected');
        if (!status) return;

        if (status === 'success') {
            toast.success('Zettle är anslutet — försäljningen bokförs automatiskt från och med nu.');
        } else {
            const reason = params.get('reason');
            toast.error(CALLBACK_REASONS[reason] || 'Anslutningen till Zettle misslyckades.');
        }
        history.replace(location.pathname);
        load();
    }, [location.search, location.pathname, history, load]);

    const onConnect = async () => {
        setConnecting(true);
        try {
            const { authorizationUrl } = await startConnect(companyId);
            // Full-page redirect, not a popup: popups break on mobile Safari
            // and behind blockers, and this is the same shape as the bank flow.
            window.location.assign(authorizationUrl);
        } catch (err) {
            const code = err?.response?.data?.code;
            toast.error(
                code === 'not_configured'
                    ? 'Zettle-integrationen är inte aktiverad i den här miljön.'
                    : 'Kunde inte starta kopplingen mot Zettle.'
            );
            setConnecting(false);
        }
    };

    const onDisconnect = async (connection) => {
        setBusyId(connection.connectionId);
        try {
            await disconnect(companyId, connection.connectionId);
            toast.success('Zettle är frånkopplat. Redan hämtad försäljning finns kvar.');
            await load();
        } catch {
            toast.error('Kunde inte koppla från Zettle.');
        } finally {
            setBusyId(null);
        }
    };

    if (loading) {
        return (
            <div className="modern-card p-4 text-center">
                <Spinner animation="border" size="sm" className="me-2" />
                Hämtar Zettle-anslutningar…
            </div>
        );
    }

    const active = connections.filter((c) => c.status !== 'disconnected');

    return (
        <div className="zettle-settings">
            <SettingsCard
                icon={CreditCard}
                title="Zettle"
                subtitle="Koppla ditt Zettle-konto så bokförs kortförsäljningen automatiskt — en gemensam verifikation per dag och betalmedel, plus dagens avgifter."
                actions={(
                    <Button variant="primary" onClick={onConnect} disabled={connecting}>
                        {connecting
                            ? <><Spinner animation="border" size="sm" className="me-2" />Öppnar Zettle…</>
                            : <><Plus size={16} className="me-2" />Anslut Zettle</>}
                    </Button>
                )}
            >
                {/* Keyed on connections, not on `active`: a disconnected account
                    still renders its own card below, so keying on active meant
                    the page said "nothing is connected" directly above a card
                    describing the connection. */}
                {connections.length === 0 ? (
                    <EmptyState
                        icon={CreditCard}
                        title="Inget Zettle-konto är anslutet ännu"
                        description="Anslut ditt Zettle-konto för att bokföra kortförsäljningen automatiskt."
                        actions={(
                            <Button variant="primary" onClick={onConnect} disabled={connecting}>
                                <Plus size={16} className="me-2" />
                                Anslut Zettle
                            </Button>
                        )}
                    />
                ) : active.length === 0 && (
                    <p className="section-description mb-0">
                        Inget aktivt Zettle-konto — kontot nedan är frånkopplat.
                    </p>
                )}
            </SettingsCard>

            {connections.map((connection) => {
                const status = statuses[connection.connectionId];
                const queue = review[connection.connectionId];
                const counts = status?.counts;
                const badge = STATUS_BADGES[connection.status];
                const subBadge = SUBSCRIPTION_BADGES[connection.subscriptionStatus];

                return (
                    <SettingsCard
                        key={connection.connectionId}
                        icon={CreditCard}
                        title={(
                            <>
                                {connection.merchantName || 'Zettle-konto'}
                                {badge && <Badge bg={badge.variant} className="ms-2">{badge.label}</Badge>}
                                {subBadge && <Badge bg={subBadge.variant} className="ms-2">{subBadge.label}</Badge>}
                            </>
                        )}
                        subtitle={`Senast hämtat: ${fmtDateTime(connection.lastSyncAt)}`}
                        actions={(
                            <span className="settings-icon-actions">
                                <Button
                                    variant="outline-danger"
                                    size="sm"
                                    title="Koppla från"
                                    disabled={busyId === connection.connectionId || connection.status === 'disconnected'}
                                    onClick={() => onDisconnect(connection)}
                                >
                                    <Trash2 size={14} />
                                </Button>
                            </span>
                        )}
                    >

                        {/* The cutover. Stated first and plainly, because a merchant
                            who keeps booking these sales by hand will double-book. */}
                        {connection.syncStartAt && connection.status !== 'disconnected' && (
                            // Not `info`: this theme renders alert-info magenta, which
                            // put the single most important line on the page — the one
                            // that stops the merchant double-booking — in hot pink.
                            // `primary` is the brand green used for callouts elsewhere.
                            <Alert variant="primary" className="d-flex align-items-start">
                                <Info size={16} className="me-2 mt-1 flex-shrink-0" />
                                <div>
                                    Från <strong>{fmtDate(connection.syncStartAt)}</strong> bokförs
                                    Zettle-försäljningen automatiskt — sluta bokföra den manuellt.
                                    Försäljning före det datumet hämtas inte.
                                </div>
                            </Alert>
                        )}

                        {connection.lastError && (() => {
                            const known = CONNECTION_ERRORS[connection.lastErrorCode];
                            return (
                                <Alert
                                    variant={known?.variant || 'warning'}
                                    className="d-flex align-items-start"
                                >
                                    <AlertTriangle size={16} className="me-2 mt-1 flex-shrink-0" />
                                    {known ? (
                                        <div>
                                            <strong className="d-block">{known.title}</strong>
                                            {known.body}
                                        </div>
                                    ) : (
                                        // An unmapped code: the raw message is all we
                                        // have, and hiding it would leave the merchant
                                        // with an empty warning.
                                        <div>{connection.lastError}</div>
                                    )}
                                </Alert>
                            );
                        })()}

                        {(connection.setupWarnings || []).map((warning) => (
                            <Alert variant="warning" key={warning.code} className="py-2">
                                {warning.message}
                            </Alert>
                        ))}

                        {counts && (
                            <dl className="settings-stats">
                                <div className="settings-stat">
                                    <dt>Bokförda</dt>
                                    <dd>{counts.mapped}</dd>
                                </div>
                                <div className="settings-stat">
                                    <dt>Väntar</dt>
                                    <dd>{counts.pending}</dd>
                                </div>
                                <div className={`settings-stat${counts.needsReview > 0 ? ' is-attention' : ''}`}>
                                    <dt>Att granska</dt>
                                    <dd>{counts.needsReview}</dd>
                                </div>
                            </dl>
                        )}

                        {status?.mapperRoles?.length > 0 && (
                            <div className="form-section-wrapper">
                                <div className="section-header-external">
                                    <div className="section-icon"><ListTree size={16} /></div>
                                    <div>
                                        <h3 className="section-heading-external">Konton som används</h3>
                                    </div>
                                </div>
                                <p className="section-description">
                                    Zettle-bokföringen slår upp konton via kontoplansroller — inga
                                    kontonummer är inbyggda. Använder ni ett annat konto än
                                    standardvalet, peka om rollen under{' '}
                                    <Link to={`/${companyId}/settings/chart?vy=roller`}>
                                        Bokföring → Kontoplansroller
                                    </Link>
                                    , så bokförs Zettle dit.
                                </p>
                                <div className="modern-table-container">
                                    <Table responsive hover className="modern-table">
                                        <thead>
                                        <tr><th>Används till</th><th>Konto</th><th>Status</th></tr>
                                    </thead>
                                    <tbody>
                                        {status.mapperRoles.map((roleName) => {
                                            const r = (roles?.roles || []).find((x) => x.role === roleName);
                                            return (
                                                <tr key={roleName}>
                                                    <td>{ROLE_LABELS[roleName] || roleName}</td>
                                                    <td>
                                                        {r?.status === 'resolved'
                                                            ? `${r.accountNumber}${r.accountName ? ` — ${r.accountName}` : ''}`
                                                            : <span className="text-muted">—</span>}
                                                    </td>
                                                    <td>
                                                        {!r && <span className="text-muted">okänd</span>}
                                                        {r?.status === 'resolved' && (
                                                            r.confirmed
                                                                ? <Badge bg="success">Vald av er</Badge>
                                                                : <Badge bg="light" text="dark">Standard</Badge>
                                                        )}
                                                        {r?.status === 'unsupported' && (
                                                            /* Not an error: the mapper only needs this
                                                               role when the situation arises, and parks
                                                               explicitly if it ever does. */
                                                            <Badge bg="secondary">Saknas i kontoplanen</Badge>
                                                        )}
                                                        {r && r.status !== 'resolved' && r.status !== 'unsupported' && (
                                                            <Badge bg="warning">Behöver åtgärd</Badge>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                    </Table>
                                </div>
                            </div>
                        )}

                        {queue?.items?.length > 0 && (
                            <div className="form-section-wrapper">
                                <div className="section-header-external">
                                    <div className="section-icon"><RefreshCw size={16} /></div>
                                    <div>
                                        <h3 className="section-heading-external">Behöver granskas</h3>
                                    </div>
                                </div>
                                <p className="section-description">
                                    Det här behöver en titt. Rader märkta <em>Ej bokförd</em> har hämtats
                                    men inte bokförts — bokför dem manuellt, eller åtgärda orsaken och låt
                                    nästa hämtning ta dem. Rader märkta <em>Bokförd</em> är bokförda, men
                                    något stämmer inte och bör kontrolleras.
                                </p>
                                <div className="modern-table-container">
                                    <Table responsive hover className="modern-table">
                                        <thead>
                                        <tr>
                                            <th>Datum</th>
                                            <th>Typ</th>
                                            <th className="text-end">Belopp</th>
                                            <th>Status</th>
                                            <th>Orsak</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {queue.items.map((item) => (
                                            <tr key={item.canonicalEventId}>
                                                <td>{fmtDate(item.businessDate)}</td>
                                                <td>{item.paymentType || item.eventKind}</td>
                                                <td className="text-end">{fmtAmount(item.amountMinor)} kr</td>
                                                <td>
                                                    {/* A booked-but-flagged row (an unreconciled payout) is
                                                        not the same ask as an unbooked one, and conflating
                                                        them would send the user hunting for a posting that
                                                        already exists. */}
                                                    {item.mappingStatus === 'mapped'
                                                        ? <Badge bg="info">Bokförd</Badge>
                                                        : <Badge bg="secondary">Ej bokförd</Badge>}
                                                </td>
                                                <td>
                                                    {REVIEW_REASONS[item.reviewReason] || item.reviewReason}
                                                    {item.reviewDetail && (
                                                        <div className="text-muted small">{item.reviewDetail}</div>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    </Table>
                                </div>
                            </div>
                        )}
                    </SettingsCard>
                );
            })}
        </div>
    );
};

export default ZettleSettings;
// Exported for the date-parsing regression test.
export { toDate };
